import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from "@heroui/react";
import {
  ContactType,
  ContractContact,
  ContractMilestoneDate,
  MilestoneDateType,
  RentalContract,
} from "shared/types/rental-contracts";
import ColoredDot from "../operationalDashboard/components/coloredDot";
import { UnitDTO } from "shared/types/unit";
import { formatDate } from "shared/utils/get-dates";
import {
  Eye,
  Flag,
  Folder,
  Info,
  User,
  Calendar,
} from "@phosphor-icons/react";
import DocumentGeneratorComponent from "components/documents/documentGeneratorComponent";
import { DocumentCategory } from "components/documents/documentGeneratorModal";
import { RenderPrice } from "./renderPrice";
import { TenantContactDetails } from "shared/components/rental/TenantContactDetails";

export const ArchivedContractsTable = ({ archivedContracts, rentalInfo }: { archivedContracts: RentalContract[], rentalInfo: any }) => {
  return (
    <Table
      aria-label="Archived contracts table"
      classNames={{
        wrapper: "shadow-none rounded-none p-0",
        thead: "h-fit border-b-2 border-neutral-primary/40",
        th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
        tbody: "border-b divide-y divide-neutral-primary/15",
        tr: "divide-x divide-neutral-primary/15",
        td: "py-2.5",
      }}
    >
      <TableHeader>
        <TableColumn>Acties</TableColumn>
        <TableColumn>Documentgenerator</TableColumn>
        <TableColumn className="min-w-[180px]">Unit</TableColumn>
        <TableColumn>Due diligence</TableColumn>
        <TableColumn>Huurder</TableColumn>
        <TableColumn>Contactgegevens</TableColumn>
        <TableColumn>Fase</TableColumn>
        <TableColumn>Startdatum</TableColumn>
        <TableColumn>Juridische vorm</TableColumn>
        <TableColumn>Aantal bewoners</TableColumn>
        <TableColumn>Openstaand</TableColumn>
        <TableColumn>Geïndexeerde huurprijs</TableColumn>
        <TableColumn>Provisies</TableColumn>
        <TableColumn>OV</TableColumn>
        <TableColumn>Gebouw attributen</TableColumn>
        <TableColumn>Facturatiefrequentie</TableColumn>
      </TableHeader>
      <TableBody>
        {archivedContracts &&
          archivedContracts.map((contract, index) => (
            <TableRow key={index}>
              <TableCell>
                <div className="flex cursor-pointer items-center gap-3">
                  <Tooltip content="Details bekijken">
                    <a
                      className="text-moss-primary"
                      onClick={() =>
                        rentalInfo.handleOpenViewContractModal(contract)
                      }
                    >
                      <Eye weight="duotone" size={22} />
                    </a>
                  </Tooltip>
                  <Tooltip 
                    content={
                      contract?.flaggedForWatchlist ? (
                        <div className="max-w-[200px]">
                          <p className="mb-1">{contract.flaggedForWatchlistReason}</p>
                          <p className="text-sm text-neutral-secondary">Klik om van de watchlist te verwijderen</p>
                        </div>
                      ) : "Op watchlist zetten"
                    }
                  >
                    <Flag 
                      className={`${contract?.flaggedForWatchlist ? "text-brick-primary" : ""}`} 
                      weight="duotone" 
                      size={22} 
                      onClick={() => rentalInfo.handleFlagForWatchList(contract)} 
                    />
                  </Tooltip>
                  {contract?.unit && <Tooltip content="Open unitfolder op SharePoint">
                    <a
                      href={contract?.unit?.sharePointFolderUrl}
                      target="_blank"
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-primary/20 text-neutral-primary"
                    >
                      <Folder />
                    </a>
                  </Tooltip>}
                  {rentalInfo.hasUpcomingPriceChanges(contract) && (
                    <Tooltip content="Contract heeft aankomende prijswijzigingen">
                      <Calendar weight="duotone" size={20} className="text-warning" />
                    </Tooltip>
                  )}
                  {rentalInfo.isNearIndexation(contract) && (
                    <Tooltip content="Contract nadert indexatiedatum">
                      <Calendar weight="duotone" size={20} className="text-brick-primary" />
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <DocumentGeneratorComponent
                  incomingContract={contract as RentalContract}
                  incomingUnit={contract.unit as UnitDTO}
                  incomingDocumentCategory={DocumentCategory.CONTRACT}
                  size="sm"
                />
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <a 
                    href={`/unit/${contract?.unit?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-moss-primary hover:text-moss-primary/80 transition-colors font-medium"
                  >
                    {`${contract?.unit?.id} ${contract?.unit?.referencePlus ? `(${contract?.unit?.referencePlus})` : ''}`}
                  </a>
                  <Tooltip
                    content={
                      ("Database contract id: " + contract.id) as any
                    }
                  >
                    <Info weight="duotone" size={18} />
                  </Tooltip>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex cursor-pointer items-center justify-center rounded-lg">
                  <ColoredDot
                    color="green"
                    number={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any).green
                          .count) ||
                      0
                    }
                    labels={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any).green
                          .labels) ||
                      []
                    }
                  />
                  <ColoredDot
                    color="orange"
                    number={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any).orange
                          .count) ||
                      0
                    }
                    labels={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any).orange
                          .labels) ||
                      []
                    }
                  />
                  <ColoredDot
                    color="red"
                    number={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any).red
                          .count) ||
                      0
                    }
                    labels={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any).red
                          .labels) ||
                      []
                    }
                  />
                  <ColoredDot
                    color="transparent"
                    number={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any)
                          .transparent.count) ||
                      0
                    }
                    labels={
                      (contract.operationalChecks &&
                        (contract.operationalChecks as any)
                          .transparent.labels) ||
                      []
                    }
                  />
                </div>
              </TableCell>
              <TableCell>
                <Chip
                  className="gap-1"
                  startContent={
                    <User
                      weight="duotone"
                      size={14}
                      className="text-neutral-secondary-mid"
                    />
                  }
                  classNames={{
                    base: "h-fit bg-white px-2 border border-neutral-secondary-mid text-neutral-secondary-mid",
                    content: "px-0 font-medium",
                  }}
                >
                  {contract.contractContacts &&
                    contract.contractContacts
                      .filter(
                        (contact: ContractContact) =>
                          contact.contactType == ContactType.TENANT,
                      )
                      .map((contact: any) => contact.resource?.name)
                      .join(", ")}
                </Chip>
              </TableCell>
              <TableCell>
                <TenantContactDetails
                  contacts={(contract.contractContacts ?? []).filter(
                    (contact: ContractContact) =>
                      contact.contactType == ContactType.TENANT,
                  )}
                />
              </TableCell>
              <TableCell>{contract.phase}</TableCell>
              <TableCell>
                {contract.contractMilestoneDates &&
                  contract.contractMilestoneDates.filter(
                    (milestone: ContractMilestoneDate) =>
                      milestone.milestoneDateType ==
                      MilestoneDateType.START_DATE,
                  ) &&
                  formatDate(
                    new Date(
                      (contract.contractMilestoneDates as any).filter(
                        (milestone: ContractMilestoneDate) =>
                          milestone.milestoneDateType ==
                          MilestoneDateType.START_DATE,
                      )[0]?.date,
                    ),
                  )}
              </TableCell>
              <TableCell>{contract.legalForm}</TableCell>
              <TableCell>{contract.numResidents}</TableCell>
              <TableCell className="font-medium">
                Aan te vullen
              </TableCell>
              <TableCell>
                <RenderPrice
                  price={contract.activePrice}
                  label="Huurprijs"
                  contract={contract}
                  rentalInfo={rentalInfo}
                  isArchived={true}
                />
              </TableCell>
              <TableCell>
                <RenderPrice
                  price={contract.activeProvision}
                  label="Provisie"
                  contract={contract}
                  rentalInfo={rentalInfo}
                  isArchived={true}
                />
              </TableCell>
              <TableCell>
                <RenderPrice
                  price={contract.activePropertyTaxes}
                  label="Onroerende voorheffing"
                  contract={contract}
                  rentalInfo={rentalInfo}
                  isArchived={true}
                />
              </TableCell>
              <TableCell>{rentalInfo.renderBuildingAttributeRevenue(contract)}</TableCell>
              <TableCell>{contract.invoicingFrequency}</TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};
